import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ShoppingBasket from './shoppingbasket';
import { removeOrder, sendOrders, sendOrdersCombined, saveOrderToStore, autosaveOrder } from '../modules/orderdata';
import { getLocationFull } from '../modules/locationdata';
import { uniq } from 'lodash';

const autosaveTimeout = 1000;
const retryTimeout = 3000;

export default function ShoppingBasketContainer(props) {
    const dispatch = useDispatch();
    const pendingOrders = useSelector((state) => state.orderdata.pendingOrders);
    const sendingOrdersInProgress = useSelector((state) => state.orderdata.sendingOrdersInProgress);
    const orderSendFailed = useSelector((state) => state.orderdata.orderSendFailed);
    const savingCombinedOrder = useSelector((state) => state.orderdata.savingCombinedOrder);
    const autosaving = useSelector((state) => state.orderdata.autosaving);
    const autosavingFailed = useSelector((state) => state.orderdata.autosavingFailed);
    const autosavingRef = useRef(false);
    const sendingOrdersInProgressRef = useRef(false);
    const pendingOrdersRef = useRef([]);
    const [unsavedChanges, setUnsavedChanges] = useState(false);

    const locationsById = useSelector((state) => state.locationdata.locationsById);
    const userProfile = useSelector((state) => state.authentication.userProfile);
    const parameters = useSelector((state) => state.parameterdata.parameters);
    const dirtyOrders = useRef([]);
    const timerHandle = useRef(null);

    useEffect(() => {
        if (Object.keys(autosaving).length) {
            autosavingRef.current = true;
        } else {
            autosavingRef.current = false;
        }
    }, [autosaving]);

    useEffect(() => {
        pendingOrdersRef.current = pendingOrders;
    }, [pendingOrders]);

    useEffect(() => {
        sendingOrdersInProgressRef.current = sendingOrdersInProgress;
    }, [sendingOrdersInProgress]);

    const saveDirtyOrders = useCallback(() => {
        try {
            if (dirtyOrders.current.length === 0) return;

            const ordersToSave = uniq(dirtyOrders.current).map((id) => {
                return pendingOrdersRef.current.find((o) => o.id === id);
            });
            dirtyOrders.current = [];
            timerHandle.current = null;

            for (let o of ordersToSave) {
                autosaveOrder(o)(dispatch);
            }
            setUnsavedChanges(false);
        } catch (error) {
            console.error('Autosave failed:', error);
        }
    }, [dispatch]);

    const doAutosave = useCallback(() => {
        if (!autosavingRef.current && !sendingOrdersInProgressRef.current) {
            saveDirtyOrders();
        } else if (!sendingOrdersInProgressRef.current) {
            timerHandle.current = setTimeout(doAutosave, autosaveTimeout);
        }
    }, [saveDirtyOrders]);

    useEffect(() => {
        return () => {
            if (timerHandle.current) {
                saveDirtyOrders();
                clearTimeout(timerHandle.current);
            }
        };
    }, [saveDirtyOrders]);

    useEffect(() => {
        if (Object.keys(autosavingFailed).length) {
            for (const id of Object.keys(autosavingFailed)) {
                dirtyOrders.current.push(parseInt(id));
            }
            if (!timerHandle.current) timerHandle.current = setTimeout(doAutosave, retryTimeout);
        }
    }, [autosavingFailed, doAutosave]);

    const removeOrderInt = (id) => {
        dispatch(removeOrder(id));
    };

    const sendOrdersInt = (orders, options, language) => {
        dispatch(sendOrders(orders, options, language));
    };

    const sendOrdersCombinedInt = (orders, options, language) => {
        dispatch(sendOrdersCombined(orders, options, language));
    };

    const getLocationFullInt = (id) => {
        dispatch(getLocationFull(id));
    };

    const sanitizeOrder = (order) => {
        if (order.orderedFilters != null && order.orderedFilters.length > 0) {
            for (let fo of order.orderedFilters) {
                fo.count = parseInt(fo.count);
            }
        }
    };

    const orderChanged = (order) => {
        order.updateTotalPrice();
        sanitizeOrder(order);
        dispatch(saveOrderToStore(order));
        setUnsavedChanges(true);
        dirtyOrders.current.push(order.id);
        if (!timerHandle.current) {
            timerHandle.current = setTimeout(doAutosave, autosaveTimeout);
        }
    };

    return (
        <ShoppingBasket
            customerView={false}
            pendingOrders={pendingOrders}
            sendingOrdersInProgress={sendingOrdersInProgress}
            orderSendFailed={orderSendFailed}
            locationsById={locationsById}
            removeOrder={removeOrderInt}
            sendOrders={sendOrdersInt}
            sendOrdersCombined={sendOrdersCombinedInt}
            getLocationFull={getLocationFullInt}
            userProfile={userProfile}
            savingCombinedOrder={savingCombinedOrder}
            parameters={parameters}
            orderChanged={orderChanged}
            autosaving={Object.keys(autosaving).length > 0}
            autosavingFailed={Object.keys(autosavingFailed).length > 0}
            unsavedChanges={unsavedChanges}
        />
    );
}
